import { RESTClient } from './client';
import { API_URL } from './apiDictionary';
import { PaginatedResponse } from '@dynamika-shared/types/general';
import {
  SiteSubjectType,
  ValidationSpecialistUploadsResponse,
  FileDownloadInfo,
  ValidationSpecialistPatientDetails,
  ValidationSpecialistPatientVisitsResponse,
  ClinicalTrialContext,
} from '@restore/ValidationSpecialistSubjects/ValidationSpecialistSubjects.types';
import { prepareUrl } from '@utils/routerHelpers';

type FetchSubjectsArgs = {
  patientCode?: string;
  page?: number;
  size?: number;
  sort?: string;
};

export const fetchValidationSpecialistSubjects = async (
  clinicalTrialId: string,
  args?: FetchSubjectsArgs
) => {
  const response = await RESTClient.get<PaginatedResponse<SiteSubjectType>>(
    API_URL.validationSpecialistPatients,
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      params: {
        patientCode: args?.patientCode || undefined,
        page: args?.page ? args?.page - 1 : 0,
        size: args?.size || 10,
        sort: args?.sort || [],
      },
    }
  );

  return response.data;
};

export const fetchClinicalTrialContext = async (
  clinicalTrialId: string
): Promise<ClinicalTrialContext> => {
  const response = await RESTClient.get<{
    configuration: { name: string; sites: { id: string; name: string; code?: string }[] };
  }>(API_URL.clinicalTrialContext, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
  });

  return {
    clinicalTrialId,
    clinicalTrialName: response.data.configuration?.name,
    sites: response.data.configuration?.sites || [],
  };
};

export const fetchValidationSpecialistSubjectDetails = async (
  clinicalTrialId: string,
  patientId: string
) => {
  const url = prepareUrl(API_URL.validationSpecialistPatientDetails, [patientId]);
  const response = await RESTClient.get<ValidationSpecialistPatientDetails>(url, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
  });

  return response.data;
};

export const fetchValidationSpecialistPatientVisits = async (
  clinicalTrialId: string,
  patientId: string
) => {
  const url = prepareUrl(API_URL.validationSpecialistPatientVisits, [patientId]);
  const response = await RESTClient.get<ValidationSpecialistPatientVisitsResponse>(url, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
  });

  return response.data;
};

export const fetchValidationSpecialistVisitUploads = async (
  clinicalTrialId: string,
  visitId: string
) => {
  const url = prepareUrl(API_URL.validationSpecialistVisitUploads, [visitId]);
  const response = await RESTClient.get<ValidationSpecialistUploadsResponse>(url, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
  });

  return response.data;
};

export const getValidationSpecialistFileDownload = async (
  clinicalTrialId: string,
  fileId: string
) => {
  const url = prepareUrl(API_URL.validationSpecialistFileDownload, [fileId]);
  const response = await RESTClient.get<FileDownloadInfo>(url, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
  });

  return response.data;
};
